<template>
  <a :href="content.link_url" target="_blank" class="war-room-post-content-link" rel="nofollow noopener noreferrer">
    <span v-if="content.link_image_url" class="war-room-post-content-link__img-wrapper">
      <NuxtImg
        class="image war-room-post-content-link__img"
        alt=""
        :src="content.link_image_url"
        :width="IMAGE_SIZE"
        :height="IMAGE_SIZE"
      />
    </span>
    <LinkIcon v-else :style="iconStyles" class="war-room-post-content-link__icon" />
    <span class="war-room-post-content-link__info">
      <span :class="urlStyles">{{ content.link_url }}</span>
      <span v-if="content.link_title" class="war-room-post-content-link__title">{{ content.link_title }}</span>
      <span v-if="content.link_description" class="war-room-post-content-link__desc">
        {{ content.link_description }}
      </span>
    </span>
  </a>
</template>

<script lang="ts" setup>
import { computed } from '@nuxtjs/composition-api';
import LinkIcon from './images/link.vue.svg';
import type { IPostContentDataLink } from '@/types/community';

type WarRoomPostContentLinkProps = {
  content: IPostContentDataLink;
};

const props = defineProps<WarRoomPostContentLinkProps>();

const IMAGE_SIZE = 64;

const isLinkOnly = computed(() => !props.content.link_title && !props.content.link_description);
const iconStyles = computed(() => ({
  width: isLinkOnly.value ? IMAGE_SIZE / 2 : IMAGE_SIZE,
  height: isLinkOnly.value ? IMAGE_SIZE / 2 : IMAGE_SIZE,
}));
const urlStyles = computed(() => ({
  'war-room-post-content-link__url': true,
  'war-room-post-content-link__url--standalone': isLinkOnly.value,
}));
</script>

<style scoped lang="scss">
.war-room-post-content-link {
  padding: 16px;
  text-decoration: none;
  display: flex;
  overflow: hidden;
  cursor: pointer;
  border-radius: 8px;
  background: var(--theme-color-over-card);
  align-items: center;
  // fix of weird bug on wide screens when flex inside doesn't shrink

  max-width: 600px;

  &__icon {
    flex-shrink: 0;
    margin-right: 16px;
  }

  &__img-wrapper {
    height: 64px;
    width: 64px;
    flex-shrink: 0;
    margin-right: 16px;
  }

  &__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  &__info {
    display: flex;
    flex-direction: column;
    overflow: hidden;
    align-items: flex-start;
  }

  &__url {
    @include caption2;
    @include text-overflow;

    color: var(--gray-50);
    width: 100%;

    &--standalone {
      @include body;
    }
  }

  &__title {
    @include body-semibold;
    @include text-overflow;

    width: 100%;
    margin-top: 4px;
    color: var(--theme-color-text);
  }

  &__desc {
    @include subhead;
    @include multiline-overflow;

    margin-top: 2px;
    color: var(--theme-color-sub-text);
  }
}
</style>
